/**
 * The API's error codes (build spec 3.1) turned into the German copy the student and the
 * teacher see. The API's own messages are English and for developers; nothing from the wire is
 * ever shown to a child.
 */

export const ERROR_CODES = [
  "validation",
  "unauthorized",
  "forbidden",
  "not_found",
  "invalid_credentials",
  "rate_limited",
  "chapter_reset",
  "conflict",
  "internal",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export class ApiClientError extends Error {
  constructor(
    readonly code: ErrorCode | "offline",
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && (ERROR_CODES as readonly string[]).includes(value);
}

/** Parses the `{ error: { code, message } }` envelope; anything else becomes `internal`. */
export async function toClientError(response: Response): Promise<ApiClientError> {
  let code: ErrorCode = "internal";
  let message = `request failed with ${response.status}`;
  try {
    const body = (await response.json()) as { error?: { code?: unknown; message?: unknown } };
    if (isErrorCode(body.error?.code)) code = body.error.code;
    if (typeof body.error?.message === "string") message = body.error.message;
  } catch {
    // A non-JSON body (a proxy error page, say) keeps the defaults above.
  }
  return new ApiClientError(code, response.status, message);
}

const GERMAN: Record<ErrorCode | "offline", string> = {
  validation: "Das hat nicht geklappt. Versuche es nochmal.",
  unauthorized: "Du bist nicht mehr angemeldet.",
  forbidden: "Dazu hast du keine Berechtigung.",
  not_found: "Das gibt es nicht.",
  invalid_credentials: "Der PIN stimmt nicht.",
  rate_limited: "Zu viele Versuche. Warte eine Minute.",
  chapter_reset: "Dieses Thema ist nicht mehr da. Es geht mit einem neuen weiter.",
  conflict: "Das hat nicht geklappt. Versuche es nochmal.",
  internal: "Da ist etwas schiefgelaufen.",
  offline: "Keine Verbindung.",
};

export function germanMessage(error: unknown): string {
  if (error instanceof ApiClientError) return GERMAN[error.code];
  return GERMAN.internal;
}
